import React, { useState, useRef, useEffect, useCallback } from 'react';
import { isEmbedded } from '../../shared/utils/embedDetection';
import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BASE_URL } from '../api/client';
import { getTokens } from '../../shared/utils/secureTokenStorage';

// Generate unique session ID
const generateSessionId = () => `support-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// All available questions organized by category
const allQuestions = [
  // Creating Cases
  "How do I create a new case?",
  "How to start a new loan application?",
  "What information do I need to create a case?",
  "How do I add a new borrower?",
  "What loan types can I select?",
  "Can I edit case details after creating it?",
  "Where do I find the new case button?",
  "How to create a case for a refinance?",
  "Is borrower email required when creating a case?",
  "What happens after I create a case?",
  "How do I name my cases?",
  "Can I delete a case?",
  "How to duplicate a case?",
  "Where are my cases listed?",
  "How do I find a specific case?",
  // Uploading Documents
  "How do I upload documents?",
  "What file types are supported?",
  "What is the maximum file size?",
  "How to upload a PDF?",
  "Can I drag and drop files?",
  "What documents do I need to upload?",
  "How to upload W2 forms?",
  "How to upload bank statements?",
  "Can I upload images of documents?",
  "How do I upload multiple documents at once?",
  "Where do I find the upload button?",
  "How long does document processing take?",
  "Can I delete an uploaded document?",
  "How to replace an uploaded document?",
  "What income documents should I upload?",
  // AI Reports
  "How do I generate an AI report?",
  "What is the difference between Basic and Advanced report?",
  "How much does an AI report cost?",
  "How long does the AI report take?",
  "What does the AI report include?",
  "How to run an underwriting analysis?",
  "Can I regenerate an AI report?",
  "What documents are needed for AI analysis?",
  "How do I view my AI report?",
  "Where do I find completed reports?",
  "What is DTI calculation?",
  "How does the AI identify red flags?",
  "Can I download the AI report?",
  "How accurate is the AI analysis?",
  "What if the AI report is missing information?",
  "How to get a more detailed report?",
  "When should I use Basic vs Advanced report?",
  "Does the AI report check for fraud?",
  "Can the AI analyze tax returns?",
  "How does income verification work?",
  // Case Assistant / Chat
  "How do I use the Case Assistant?",
  "What can I ask the chat?",
  "How to search my documents?",
  "Can the AI summarize my case?",
  "How do I ask about borrower income?",
  "Does the chat remember my conversation?",
  "How much does each question cost?",
  "Can I ask follow-up questions?",
  // Collaboration & Sharing
  "How do I share a case?",
  "How to invite a collaborator?",
  "What permission levels are available?",
  "Can I remove a collaborator?",
  "How to change collaborator permissions?",
  "Where do I see shared cases?",
  "How does the invitation work?",
  "Can collaborators edit my case?",
  "How to resend an invitation?",
  "Can I share with people outside my company?",
  // Subscription & Credits
  "What subscription plans are available?",
  "How much does each plan cost?",
  "How do credits work?",
  "How many credits do I have?",
  "How to check my credit balance?",
  "When do credits refresh?",
  "How to buy more credits?",
  "How to upgrade my plan?",
  "How to downgrade my plan?",
  "What features are included in Pro?",
  "Do unused credits roll over?",
  "How to cancel my subscription?",
  "Is there a free trial?",
  "What is included in the free plan?",
  // Troubleshooting - AI Reports
  "Why did my AI report fail?",
  "My report is stuck on processing",
  "AI report taking too long",
  "Report generation error",
  "How to fix a failed report?",
  "Report has been processing for 10 minutes",
  "Why is my report incomplete?",
  "AI report shows wrong information",
  "How to cancel a running report?",
  "Report failed with no error message",
  // Troubleshooting - Documents & General
  "Document upload failed",
  "File won't upload",
  "Upload error message",
  "Can't open uploaded document",
  "Document shows as corrupted",
];

// Get random questions from the pool
const getRandomQuestions = (count = 3, exclude = []) => {
  const available = allQuestions.filter(q => !exclude.includes(q));
  const shuffled = [...available].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

// Safe text renderer - converts markdown-like syntax to React elements
const renderMessageContent = (content) => {
  if (!content) return null;

  const lines = content.split('\n');
  return lines.map((line, lineIndex) => {
    // Process bold text **text**
    const parts = [];
    let remaining = line;
    let keyIndex = 0;

    while (remaining.includes('**')) {
      const startIdx = remaining.indexOf('**');
      if (startIdx > 0) {
        parts.push(<span key={`${lineIndex}-${keyIndex++}`}>{remaining.slice(0, startIdx)}</span>);
      }
      remaining = remaining.slice(startIdx + 2);
      const endIdx = remaining.indexOf('**');
      if (endIdx === -1) {
        parts.push(<span key={`${lineIndex}-${keyIndex++}`}>**{remaining}</span>);
        remaining = '';
        break;
      }
      parts.push(<strong key={`${lineIndex}-${keyIndex++}`}>{remaining.slice(0, endIdx)}</strong>);
      remaining = remaining.slice(endIdx + 2);
    }

    if (remaining) {
      parts.push(<span key={`${lineIndex}-${keyIndex++}`}>{remaining}</span>);
    }

    return (
      <React.Fragment key={lineIndex}>
        {parts.length > 0 ? parts : line}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

export default function SupportChatBot() {
  // isEmbedded is imported from App.js for consistent detection across all components

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hi! I'm your CloudMortgage.io Support assistant. I can help you with:\n\n• Creating and managing cases\n• Uploading documents\n• AI reports and credits\n• Troubleshooting issues\n\nWhat do you need help with?",
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => {
    const stored = sessionStorage.getItem('support_session_id');
    if (stored) return stored;
    const newId = generateSessionId();
    sessionStorage.setItem('support_session_id', newId);
    return newId;
  });
  const [thinkingStep, setThinkingStep] = useState(null);
  const [displayedQuestions, setDisplayedQuestions] = useState(() => getRandomQuestions(3));

  // Rotate questions to show new ones
  const rotateQuestions = useCallback(() => {
    setDisplayedQuestions(getRandomQuestions(3, displayedQuestions));
  }, [displayedQuestions]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatPanelRef = useRef(null);
  const chatButtonRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    // When embedded in iframe, completely disable auto-scroll to prevent parent page scroll
    if (isEmbedded) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isEmbedded]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    // When embedded, skip auto-focus entirely to prevent any scroll issues
    if (isEmbedded) return;
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isEmbedded]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        chatPanelRef.current &&
        !chatPanelRef.current.contains(event.target) &&
        chatButtonRef.current &&
        !chatButtonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setThinkingStep({ title: 'Searching help docs', status: 'in_progress' });

    const assistantMessageId = `assistant-${Date.now()}`;
    let assistantContent = '';

    try {
      // Get auth token
      const tokens = getTokens();
      const accessToken = tokens?.accessToken;

      const response = await fetch(`${BASE_URL}/api/chat/v2/ask/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        },
        body: JSON.stringify({
          message: messageText.trim(),
          session_id: sessionId,
          mode: 'help',
        }),
      });

      // Log request for debugging
      console.log('Support Chat Request:', {
        url: `${BASE_URL}/api/chat/v2/ask/stream`,
        method: 'POST',
        body: {
          message: messageText.trim(),
          session_id: sessionId,
          mode: 'help',
        },
        hasToken: !!accessToken,
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Add empty assistant message to start streaming into
      setMessages((prev) => [
        ...prev,
        { id: assistantMessageId, role: 'assistant', content: '' },
      ]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter((line) => line.startsWith('data: '));

        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'thinking_step') {
              setThinkingStep({ title: data.title, status: data.status });
              if (data.status === 'completed') {
                setTimeout(() => setThinkingStep(null), 500);
              }
            } else if (data.type === 'chunk') {
              assistantContent += data.content;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: assistantContent }
                    : msg
                )
              );
            } else if (data.type === 'done') {
              setThinkingStep(null);
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    } catch (error) {
      console.error('Support chat error:', error);
      setThinkingStep(null);

      // Fallback response
      setMessages((prev) => [
        ...prev.filter((msg) => msg.id !== assistantMessageId),
        {
          id: assistantMessageId,
          role: 'assistant',
          content:
            "I'm sorry, I'm having trouble connecting right now. Please try again or contact us at **support@cloudmortgage.io** for immediate assistance.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickQuestion = (question) => {
    sendMessage(question);
  };

  return (
    <>
      {/* Floating Chat Button */}
      <motion.button
        ref={chatButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(14, 165, 233, 0.4)',
          zIndex: 9999,
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? (
          <X size={28} color="#fff" />
        ) : (
          <MessageCircle size={28} color="#fff" />
        )}
      </motion.button>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={chatPanelRef}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              bottom: '100px',
              right: '24px',
              width: '380px',
              maxWidth: 'calc(100vw - 48px)',
              height: '620px',
              maxHeight: 'calc(100vh - 120px)',
              borderRadius: '16px',
              background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.98), rgba(12, 18, 32, 0.98))',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 24px 80px rgba(8, 12, 24, 0.6)',
              backdropFilter: 'blur(20px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              zIndex: 9998,
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid rgba(148, 163, 184, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(56, 189, 248, 0.05)',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Bot size={22} color="#fff" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#e2e8f0' }}>
                  CloudMortgage.io Support
                </h3>
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                  Ask me anything
                </p>
              </div>
            </div>

            {/* Messages */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                    flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background:
                        message.role === 'user'
                          ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                          : 'linear-gradient(135deg, #0ea5e9, #6366f1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {message.role === 'user' ? (
                      <User size={16} color="#fff" />
                    ) : (
                      <Bot size={16} color="#fff" />
                    )}
                  </div>
                  <div
                    style={{
                      maxWidth: '75%',
                      padding: '12px 14px',
                      borderRadius: '12px',
                      background:
                        message.role === 'user'
                          ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.15))'
                          : 'rgba(255, 255, 255, 0.05)',
                      border:
                        message.role === 'user'
                          ? '1px solid rgba(34, 197, 94, 0.3)'
                          : '1px solid rgba(148, 163, 184, 0.15)',
                      color: '#e2e8f0',
                      fontSize: '14px',
                      lineHeight: 1.5,
                    }}
                  >
                    {renderMessageContent(message.content)}
                  </div>
                </div>
              ))}

              {/* Thinking indicator */}
              {thinkingStep && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: 'rgba(56, 189, 248, 0.1)',
                    border: '1px solid rgba(56, 189, 248, 0.2)',
                    fontSize: '13px',
                    color: '#38bdf8',
                  }}
                >
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  {thinkingStep.title}...
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions - Rotating suggestions */}
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid rgba(148, 163, 184, 0.1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}
              >
                <span style={{ fontSize: '11px', color: '#64748b' }}>Suggested questions</span>
                <button
                  onClick={rotateQuestions}
                  disabled={isLoading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#38bdf8',
                    fontSize: '11px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  ↻ More
                </button>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                {displayedQuestions.map((q) => (
                  <button
                    key={q}
                    onClick={() => handleQuickQuestion(q)}
                    disabled={isLoading}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(56, 189, 248, 0.2)',
                      background: 'rgba(56, 189, 248, 0.05)',
                      color: '#94a3b8',
                      fontSize: '13px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.5 : 1,
                      transition: 'all 0.2s',
                      textAlign: 'left',
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              style={{
                padding: '12px 16px',
                borderTop: '1px solid rgba(148, 163, 184, 0.15)',
                display: 'flex',
                gap: '10px',
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your question..."
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: '#e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  border: 'none',
                  background:
                    inputValue.trim() && !isLoading
                      ? 'linear-gradient(135deg, #0ea5e9, #6366f1)'
                      : 'rgba(148, 163, 184, 0.2)',
                  color: inputValue.trim() && !isLoading ? '#fff' : '#64748b',
                  cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isLoading ? (
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </form>

            {/* Footer */}
            <div
              style={{
                padding: '8px 16px',
                textAlign: 'center',
                fontSize: '11px',
                color: '#64748b',
                borderTop: '1px solid rgba(148, 163, 184, 0.1)',
              }}
            >
              Need more help?{' '}
              <a
                href="mailto:support@cloudmortgage.io"
                style={{ color: '#38bdf8', textDecoration: 'none' }}
              >
                support@cloudmortgage.io
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
